import type { IInstrument } from '@app/domain/instrument/instrument.interface'

import type {
  InteractionStatusId,
  TransmissionStatusId,
} from '../interaction/interaction.interface'
import type { IUsePagination } from '../pagination/pagination.interface'
import type {
  IGetPatientOptions,
  IPatient,
  IPatientId,
} from '../patient/patient.interface'
import type { PdfTemplate } from '../visit/visit.interface'

export type IInteractionDate = Date
export type IProgramId = string
export type IProgramInteractionId = string

export const selectInteractionInstrumentsModalTittle =
  'Select Instruments for New Interaction'
export const editInteractionInstrumentsModalTittle =
  'Modify Interaction Instruments'

export const selectConfigurationForInteractionModalTittle =
  'Select Configuration for New Interaction'
export const editConfigurationforInteractionModalTittle =
  'Edit Configuration for Interaction'

export enum RequestStatusId {
  Unsubmitted = 'unsubmitted',
  Error = 'error',
  Success = 'success',
}

export enum ProgramInteractionTypeId {
  AdHocInteraction = 'ad_hoc_interaction',
  FollowUpInteraction = 'follow_up_interaction',
  InitialInteraction = 'initial_interaction',
  RemoteInteraction = 'remote_interaction',
}

export enum ScheduleType {
  DaysInAdvance = 'days_in_advance',
  DaysPastDue = 'days_past_due',
}

export enum IInteractionStatusId {
  Active = 'active',
  Removed = 'removed',
}

export enum IRemoteWorkflowStatusId {
  Completed = 'completed',
  Cancelled = 'cancelled',
  Delivered = 'delivered',
  Pending = 'pending',
  InProgress = 'in-progress',
  Expired = 'expired',
}

export interface IProgramInteraction {
  daysAfterInitialInteraction?: number
  expectedInteractionDate: IInteractionDate
  id: IProgramInteractionId
  instruments: IInstrument[]
  interactionCompletedAt?: IInteractionDate
  interactionDate?: IInteractionDate
  interactionType: ProgramInteractionTypeId
  label?: string
  pdfTemplates: PdfTemplate[]
  status: IInteractionStatusId
  removedAt?: IInteractionDate
  visitDefinitionId?: string
}

export interface IProgramInteractionRecord {
  interaction: IProgramInteraction
  patientId: string
  mrn: string
  familyName: string
  givenName: string
  interactionCompletedAt?: IInteractionDate
  remotePatientWorkflowId?: string
  remoteInteractionId?: string
  status: InteractionStatusId
  transmissionStatus: TransmissionStatusId
  remoteWorkflowStatus: IRemoteWorkflowStatusId
  remoteWorkflowStatusUpdatedAt: Date
  visitId?: string
  deliveredAt: IInteractionDate
  consentsToEmail: boolean
  consentsToSms: boolean
  phoneNumber?: string
  phoneNumberType?: string
  emailAddress?: string
}

export interface IProgramStatistics {
  enrolledPatients: number
  completedInteractions: number
  pendingInteractions: number
}

export interface IPatientProgramInteraction {
  daysAfterInitialInteraction?: number
  deliveredAt?: IInteractionDate
  expectedInteractionDate: IInteractionDate
  instruments: IInstrument[]
  interactionCompletedAt?: IInteractionDate
  interactionDate?: IInteractionDate
  interactionStatus: IInteractionStatusId
  interactionType: ProgramInteractionTypeId
  label?: string
  programInteractionId: IProgramInteractionId
  remotePatientWorkflowId?: string
  remoteInteractionId?: string
  removedAt?: IInteractionDate
  status: InteractionStatusId
  transmissionStatus?: TransmissionStatusId
  remoteWorkflowStatus?: IRemoteWorkflowStatusId
  visitId?: string
  consentsToEmail: boolean
  consentsToSms: boolean
  phoneNumber?: string
  phoneNumberType?: string
  emailAddress?: string
}

export interface IProgram {
  programId: IProgramId
  name: string
  interactionCompleteContent?: string
  interactionInformationContent?: string
  interactionNoticeEmailContent?: string
  interactionNoticeSmsContent?: string
  interactions: Record<ProgramInteractionTypeId, IProgramInteraction[]>
  remoteInteractionExpirationPeriod: string
}

export enum ProgramEnrolmentStatusId {
  Discontinued = 'discontinued',
  Enroled = 'enroled',
  Removed = 'removed',
}

export type IGetProgramEnrolmentOptions = Omit<
  IGetPatientOptions,
  'inProgramId' | 'notInProgramId'
> & {
  deliverable?: boolean
}

export interface IUseProgramStatistics {
  statistics: IProgramStatistics | null
  isLoading: boolean
  fetchStatistics: (programId: IProgramId) => Promise<void>
}

export interface IUseProgramEnrolments
  extends Omit<IUsePagination, 'setPageCount' | 'setTotalNumberOfRecords'> {
  addPatientsToProgram: (
    patientIds: IPatientId[],
    startDate: string,
  ) => Promise<void>
  deletePatientsFromProgram: (patientId: IPatientId) => Promise<void>
  getProgramEnrolments: () => Promise<void>
  isLoading: boolean
  programEnrolments: IProgramEnrolment[]
  searchOptions: Partial<IGetProgramEnrolmentOptions>
  setSearchOptions: (
    searchOptions: Partial<IGetProgramEnrolmentOptions>,
  ) => void
  updateEnrolmentStatus: (
    patientIds: IPatientId[],
    status: ProgramEnrolmentStatusId,
    statusReason?: string,
    statusReasonDescription?: string,
  ) => Promise<void>
}

export interface IUseProgramEnrolmentsWithPatients
  extends IUseProgramEnrolments {
  getPatients: () => Promise<void>
  patients: IPatient[]
}

export interface IProgramEnrolment {
  enrolmentDate: Date
  patientId: IPatientId
  programId: IProgramId
  program: IProgram
  programInteractions: IPatientProgramInteraction[]
  status: ProgramEnrolmentStatusId
  statusReason?: string
  statusReasonDescription?: string
  remoteInteractionId?: string
  startDate: Date
  consentsToEmail: boolean
  consentsToSms: boolean
  phoneNumber?: string
  phoneNumberType?: string
  emailAddress?: string
}

export interface IUseListPrograms {
  programs: IProgram[]
  pageCount: number
  totalNumberOfRecords: number
  isLoading: boolean
  getPrograms: (pageNumber?: number, recordsPerPage?: number) => void
}

export interface IUseProgram {
  program: IProgram
  isLoading: boolean
  errorMessage: string
  clearError: () => void
  addReportTemplate: (
    programInteractionId: string,
    templateId: string,
    label?: string,
  ) => Promise<void>
  deleteReportTemplate: (
    programInteractionId: string,
    templateId: string,
  ) => Promise<void>
  addProgramInteraction: (
    programInteractionType: string,
    instrumentIds: string[],
  ) => Promise<void>
  deleteProgramInteraction: (programInteractionId: string) => Promise<void>
  removeProgramInteraction: (programInteractionId: string) => Promise<void>
  updateProgramInteraction: (
    programInteractionId: string,
    instrumentIds: string[],
  ) => Promise<void>
  updateProgramInteractionProperties: (
    programInteractionId: string,
    label?: string,
    daysAfterInitialInteraction?: number,
  ) => Promise<void>
  addProgramInteractionWithDefinitionId: (
    interactionType: ProgramInteractionTypeId,
    visitDefinitionId: string,
  ) => Promise<void>
  updateProgramInteractionWithDefinitionId: (
    programInteractionId: string,
    visitDefinitionId: string,
  ) => Promise<void>
}

export enum CreateProgramStatusId {
  Unsubmitted = 'unsubmitted',
  Error = 'error',
  Success = 'success',
}

export interface IUseCreateProgram {
  errorMessage: string
  status: CreateProgramStatusId
  createProgram: (name: string) => void
}

export interface IUseProgramInteractionHistory {
  getProgramInteractionHistory: (patientId: IPatientId) => void
  programInteractionHistory: Record<IProgramInteractionId, Date>
  isLoading: boolean
}

export interface IUsePatientProgramInteractionRecord {
  interactions: IProgramInteractionRecord[]
  isLoading: boolean
  revokeRemoteInteraction: (
    patientId: string,
    programInteractionId: string,
    programId: string,
    pageNumber: number,
    recordsPerPage: number,
    patientName?: string,
    mrn?: string,
    isDeliverable?: boolean,
    status?: string,
    sortField?: string,
    sortDirection?: string,
  ) => void
  fetchProgramInteractionRecords: (
    programId: IProgramId,
    pageNumber: number,
    recordsPerPage: number,
    patientName?: string,
    mrn?: string,
    isDeliverable?: boolean,
    status?: string,
    sortField?: string,
    sortDirection?: string,
  ) => void
  sendInteractionToIntakeAnywhere: (
    patientId: string,
    programInteractionId: string,
    programId: string,
    pageNumber: number,
    recordsPerPage: number,
    patientName?: string,
    mrn?: string,
    isDeliverable?: boolean,
    status?: string,
    sortField?: string,
    sortDirection?: string,
  ) => void
  total: number
}
